'use client';

import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';
import {
  FormContext,
  type FormContextType,
} from '../../../form-builder/hooks/FormContext';
import { WizardFormData } from './wizardLogic.types';

type WizardFormProviderProps = {
  formData: WizardFormData;
  setFormData: Dispatch<SetStateAction<WizardFormData>>;
  errors: Record<string, string>;
  setErrors: Dispatch<SetStateAction<Record<string, string>>>;
  corporationId?: number;
  companyId?: number;
  children: ReactNode;
};

/**
 * Self-supplied FormContext for the hardcoded onboarding wizard. It lets the real
 * Form-Builder field wrappers (NumberInput/Select/RadioGroup/CountryInput/Input) and
 * the repeatable-list sections render unchanged — so the wizard is visually identical
 * to a DSL-rendered form — while being driven by local React state instead of the
 * FormDSL submit pipeline.
 *
 * Visibility / disabled / validation are owned by the wizard's own React code, so
 * `isFieldVisible` / `isFieldDisabled` are constant and `validateField` is a no-op
 * (per-step validation runs in the renderer's handleNext). `dslContext` carries the
 * corporation / company ids the wrappers expect.
 */
export const WizardFormProvider = ({
  formData,
  setFormData,
  errors,
  setErrors,
  corporationId,
  companyId,
  children,
}: WizardFormProviderProps) => {
  const [nestedFormOpen, setNestedFormOpen] = useState<
    { fieldId: string; editingIndex: number | null } | null
  >(null);
  const [uploadingFields, setUploadingFields] = useState<Set<string>>(
    () => new Set<string>()
  );

  const value = useMemo<FormContextType>(
    () => ({
      formData,
      setFormData,
      updateField: (fieldName, fieldValue) =>
        setFormData((prev) => ({ ...prev, [fieldName]: fieldValue })),
      currentStep: 0,
      setCurrentStep: () => {},
      errors,
      setErrors: (next) => setErrors(next),
      validateField: () => null,
      isFieldVisible: () => true,
      isFieldDisabled: () => false,
      dslContext: { companyId, corporationId },
      nestedFormOpen,
      setNestedFormOpen,
      uploadingFields,
      setFieldUploading: (fieldId, isUploading) =>
        setUploadingFields((prev) => {
          const next = new Set(prev);
          if (isUploading) next.add(fieldId);
          else next.delete(fieldId);
          return next;
        }),
      componentMap: undefined,
      isPreview: false,
    }),
    [
      formData,
      setFormData,
      errors,
      setErrors,
      corporationId,
      companyId,
      nestedFormOpen,
      uploadingFields,
    ]
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};
